import React, { useState } from "react";
import clsx from "clsx";
import { isEmpty } from "lodash";
import { Grid } from "@mui/material";
import { Icons, Box, IconButton, Typography, Chip } from "@curves/ui-base";
import { EditableInfo, OverflowMenu } from "@icoach/components/";
import { CycleOrderProductsTable } from "@icoach/memberDetail/table";
import { formatCurrencyFn, isFunction } from "@util/utils";
import { displayDateText } from "@util/moment";
import PendingCycleEffectOrderList from "@icoach/members/components/PendingCycleEffectOrderList";
import CycleOrderRecordList from "@icoach/members/components/CycleOrderRecordList";
import { CYCLEORDER_TYPE } from "@constants/";
import { filterMenu, getHealthyCyclePurchaseMenuMap } from "@icoach/healthyCyclePurchase/CyclePurchase.util";

// 主單的子項目定義，如歷史與預計變更單。
const subInfoType = {
	effectOrder: "effectOrder", // 預計變更單
	history: "history", // 訂購單變更紀錄
};

// 循環訂購單
const CyclePurchaseCard = ({ className, cycleOrderData = {}, showSubInfo, handleToggle, dialogRef, refresh }) => {
	const { cycleOrderNo, cycleOrderType, cycleOrderTypeText, cycleOrderDate, firstShipmentMonth, employeeName, shippingDayText, saleAmount, effectCount, items } = cycleOrderData || {};
	const { openCyclePurchaseInvoiceDialog, openCyclePurchasePaymentDialog, openCyclePurchaseShippingDialog } = dialogRef.current || {};

	const actionMenu = filterMenu(
		getHealthyCyclePurchaseMenuMap(
			{
				memberID: cycleOrderData?.memberID,
				cycleOrderID: cycleOrderData?.cycleOrderID,
				effectID: cycleOrderData?.effectID ?? 0,
				canReview: true,
				canModify: cycleOrderData?.canModify,
				canSuspend: cycleOrderData?.canSuspend,
				canCancel: cycleOrderData?.canCancel,
				canResumeNextCycle: cycleOrderData?.canResumeNextCycle,
				canDeleted: cycleOrderData?.canDeleted,
				canEnableFirstOrder: cycleOrderData?.canEnableFirstOrder,
				canChangeEmployee: cycleOrderData?.canChangeEmployee,
			},
			{ dialogRef, refresh },
		),
	);

	return (
		<Box className={clsx("cycle-order-card", { quit: cycleOrderType === CYCLEORDER_TYPE.UNSUBSCRIBEORDER }, className)}>
			<Box className={"cycle-order-card-header"}>
				<Grid spacing={3} container>
					<Grid xs={4} item>
						<EditableInfo label={"訂單編號"} title={cycleOrderNo}>
							{cycleOrderNo}
						</EditableInfo>
					</Grid>
					<Grid xs={8} item>
						<EditableInfo label={"訂單狀態"}>
							<Typography
								className={clsx("mb-0", {
									"text-error": cycleOrderType === CYCLEORDER_TYPE.UNSUBSCRIBEORDER || cycleOrderType === CYCLEORDER_TYPE.PAUSEORDER,
								})}
							>
								{cycleOrderTypeText}
							</Typography>
						</EditableInfo>
					</Grid>
					<Grid xs={4} item>
						<EditableInfo label={"建單日期"}>{displayDateText(cycleOrderDate)}</EditableInfo>
					</Grid>
					<Grid xs={4} item>
						<EditableInfo label={"首次出貨日"}>{displayDateText(firstShipmentMonth)}</EditableInfo>
					</Grid>
					<Grid xs={4} item>
						<EditableInfo label={"建單擔當"}>{employeeName}</EditableInfo>
					</Grid>
					{!!effectCount && (
						<Grid xs={4} item>
							<EditableInfo label={"預計變更數量"}>
								<Chip label={effectCount} color={"error"} onClick={() => handleToggle(subInfoType.effectOrder)} />
							</EditableInfo>
						</Grid>
					)}
				</Grid>
				<OverflowMenu actionButtonProps={{ className: "cycle-order-card-header-actions" }} menuData={actionMenu} enabled={!isEmpty(actionMenu)} />
			</Box>
			<CycleOrderProductsTable className={"cycle-order-card-products"} data={items} />
			<Box className={"cycle-order-card-footer"}>
				<Box className={"cycle-order-card-footer-info"}>
					<EditableInfo label={"出貨週期"}>{`${shippingDayText}日`}</EditableInfo>
					<Box component={"span"} className={"total"}>
						<Box component={"span"}>總金額</Box>
						<Box component={"span"} className={"h5"}>
							{formatCurrencyFn(saleAmount, { symbol: "$" })}
						</Box>
					</Box>
				</Box>
				<Box className={"cycle-order-card-footer-actions"}>
					<IconButton className={"icon-button-column"} onClick={() => isFunction(openCyclePurchaseShippingDialog) && openCyclePurchaseShippingDialog(cycleOrderData)}>
						<Icons.ShippingFast />
						<Typography className={"icon-button-column-text"}>宅配</Typography>
					</IconButton>
					<IconButton className={"icon-button-column"} onClick={() => isFunction(openCyclePurchasePaymentDialog) && openCyclePurchasePaymentDialog(cycleOrderData?.payment ?? {})}>
						<Icons.DollarSign />
						<Typography className={"icon-button-column-text"}>付款</Typography>
					</IconButton>
					<IconButton className={"icon-button-column"} onClick={() => isFunction(openCyclePurchaseInvoiceDialog) && openCyclePurchaseInvoiceDialog(cycleOrderData?.invoice ?? {})}>
						<Icons.FileInvoiceDollar />
						<Typography className={"icon-button-column-text"}>發票</Typography>
					</IconButton>
					<IconButton
						className={clsx("icon-button-column", {
							current: showSubInfo === subInfoType.history,
						})}
						onClick={() => handleToggle(subInfoType.history)}
					>
						<Icons.History />
						<Typography className={"icon-button-column-text"}>歷史</Typography>
					</IconButton>
					<IconButton
						className={clsx("icon-button-column", {
							current: showSubInfo === subInfoType.effectOrder,
						})}
						onClick={() => handleToggle(subInfoType.effectOrder)}
					>
						<Icons.DocumentFile />
						<Typography className={"icon-button-column-text"}>預計</Typography>
					</IconButton>
				</Box>
			</Box>
		</Box>
	);
};

const CyclePurchaseBox = ({ className, cycleOrderData, dialogRef, refresh }) => {
	const { cycleOrderID, memberID } = cycleOrderData;
	const [showSubInfo, setShowSubInfo] = useState("");

	const handleToggle = target => {
		setShowSubInfo(prevState => {
			let _target = target;
			if (prevState === target) _target = "";
			return _target;
		});
	};

	return (
		<React.Fragment>
			<CyclePurchaseCard className={className} cycleOrderData={cycleOrderData} showSubInfo={showSubInfo} handleToggle={handleToggle} dialogRef={dialogRef} refresh={refresh} />
			{showSubInfo === subInfoType.effectOrder && <PendingCycleEffectOrderList cycleOrderID={cycleOrderID} memberID={memberID} dialogRef={dialogRef} />}
			{showSubInfo === subInfoType.history && <CycleOrderRecordList sourceData={cycleOrderData} />}
		</React.Fragment>
	);
};

export default CyclePurchaseBox;
